import { useSelector } from 'react-redux';

import { Translate } from '@travel/translation';
import ItemFeatures from '@travel/traveler-core/components/ItemFeatures';
import { useDialogHandler } from '@travel/traveler-core/hooks';
import { parseSearchQuery, URL_KEYS } from '@travel/traveler-core/utils/jrdpHelper';
import { parseQueryString, stringify } from '@travel/utils';

import ImageGallery from 'components/ImageGallery';
import PriceInfo from 'components/PriceInfo';
import VacancyCalendarEntryButton from 'components/VacancyCalendar/VacancyCalendarEntryButton';
import RoomDetailPopUp from '../RoomDetailPopup';

import paths from 'constants/paths';

import { getQueryString } from 'store/__router/selectors';

import styles from './roomInfo.module.scss';

import type { HasMeal, URLSearchQuery } from 'Common-Types';
import type { Item } from 'PlanList-Types';

export type Props = {
  /** Object of specific room item  */
  item: Item;
  /** Node of icon list (extra component)  */
  iconList?: React.ReactNode;
  /** Flag to define whether the Gallery will be shown or not  */
  isShowImage: boolean;
  /** String of current selected plan id (page identifier)  */
  planId: string;
  /** String of current selected hotel id (page identifier)  */
  hotelId: string;
  /** String of current selected departure date  */
  departureDate?: string;
  /** String of current selected return date  */
  returnDate?: string;
  /** Object of current active query */
  query?: URLSearchQuery;
  /** String of accommodation area path for navigation */
  accommodationAreaPath: string;
  /** String of accommodations area paths for navigation */
  accommodationsPath: string;
  /** String for departure area code for Vacancy Calendar search */
  departureAreaCode: string;
  /** HasMeal Information for Vacancy Calendar search */
  hasMeal?: HasMeal;
  /** Default station name used for Departure, used for Vacancy Calendar search */
  departureStation?: string;
  /** Default station name used for Arrival, used for Vacancy Calendar search */
  destinationStation?: string;
};

const RoomInfo = (props: Props) => {
  const {
    item,
    iconList = null,
    isShowImage,
    planId,
    hotelId,
    departureDate,
    returnDate,
    query,
    accommodationAreaPath,
    accommodationsPath,
    departureAreaCode,
    hasMeal,
    departureStation,
    destinationStation,
  } = props;

  const {
    roomSize,
    capacity,
    itemId,
    media,
    points,
    price,
    name: roomName,
    inventoryCount,
    featureGroups,
  } = item;
  const search = useSelector(getQueryString);

  const { adultCount, childCount, noOfDays, stayNights } = parseSearchQuery(query);
  const planDetailsUrl = `${paths.planDetail.pathResolver(hotelId, planId, itemId)}?${stringify({
    ...parseQueryString(search),
    [URL_KEYS.ACCOMMODATION_AREA]: accommodationAreaPath,
    [URL_KEYS.PLACE_OF_STAY]: accommodationsPath,
  })}`;

  const {
    isOpen: isRoomDetailOpen,
    onOpen: onRoomDetailOpen,
    onClose: onRoomDetailClose,
  } = useDialogHandler(false);

  return (
    <div className={styles.roomContainer} data-testid="roomInfo-wrapper">
      {isShowImage && (
        <ImageGallery
          media={media}
          galleryType="slide"
          className={styles.media}
          galleryClassName={styles.imageWrapper}
          swiperClassName={styles.imageGallery}
        />
      )}
      <div className={styles.roomInfo}>
        {roomName && (
          <button className={styles.roomDetailButton} onClick={onRoomDetailOpen}>
            <h4 className={styles.roomTitle} data-testid="roomInfo-roomName">
              {roomName}
            </h4>
          </button>
        )}
        {iconList}
        <ItemFeatures
          features={featureGroups}
          roomSize={roomSize}
          capacity={capacity}
          className={styles.itemFeatures}
          isDisplayOtherFeatures
        />
        <div className={styles.roomLinks}>
          <button onClick={onRoomDetailOpen} className={styles.detailLink}>
            <Translate id="Plan_List.Item_Detail.Link" />
          </button>
          <VacancyCalendarEntryButton
            departureAreaCode={departureAreaCode}
            departureDate={departureDate}
            returnDate={returnDate}
            stayNights={stayNights}
            hotelId={hotelId}
            planId={planId}
            itemId={itemId}
            query={query}
            onSubmitVacancyCalendarFormPath={paths.plansList.pathResolver(hotelId)}
            hasMeal={hasMeal}
            roomCapacity={capacity}
            departureStation={departureStation}
            destinationStation={destinationStation}
          />
        </div>
      </div>
      <PriceInfo
        price={price}
        adultCount={adultCount}
        numberOfChildren={childCount}
        noOfDays={noOfDays}
        stayNights={stayNights}
        selectButtonText={<Translate id="Plan_List.List.Display_Details" />}
        points={points}
        roomCount={inventoryCount}
        className={styles.priceInfo}
        isPlanPrice
        onSelectUrl={planDetailsUrl}
      />
      {isRoomDetailOpen && (
        <RoomDetailPopUp
          title={<Translate id="Plan_List.Item_Detail.Title" />}
          isOpen={isRoomDetailOpen}
          onClose={onRoomDetailClose}
          item={item}
        />
      )}
    </div>
  );
};

export default RoomInfo;
